import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';

export const createUserPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission, usePdf, useExport, useImport } = permissions;

	const canAdmin = () => hasAnyPermission('CUSTOMERS_ADMIN');

	const canBlock = () => hasAnyPermission('CUSTOMERS_BLOCK_ADMIN');

	const userPermissions = {
		canRead: () => hasAnyPermission('CUSTOMERS_READER'),
		canCreate: canAdmin,
		canUpdate: canAdmin,
		canDelete: canAdmin,
		canArchive: canAdmin,
		canAddAttachment: (ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_WRITER') ||
			(ownUser && hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_WRITER')),
		canRemoveAttachment: (ownAttachment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_DELETER') ||
			(ownAttachment && hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_DELETER')) ||
			(ownAttachment && ownUser && hasAnyPermission('CUSTOMERS_OWN_ATTACHMENTS_OWN_DELETER')),
		canAddComment: () => hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER'),
		canEditComment: (ownComment?: boolean, ownUser?: boolean) =>
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_COMMENTS_DELETER') ||
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_DELETER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_DELETER')),
		canSeeUsers: () => userPermissions.canRead(),
		canEditUsers: canAdmin,
		canInviteUsers: canAdmin,
		canChangeRole: canAdmin,
		canEditUserGroups: canAdmin,
		canArchiveUsers: canAdmin,
		canUndoArchiveUsers: canAdmin,
		canDeleteUsers: canAdmin,
		canBlockUsers: canBlock,
		canUnblockUsers: canBlock,
		canGenerateDocument: (ownUser?: boolean) => {
			if (!usePdf) return false;
			return (
				hasAnyPermission('CUSTOMERS_DOCUMENT_GENERATOR') ||
				(ownUser && hasAnyPermission('CUSTOMERS_OWN_DOCUMENT_GENERATOR'))
			);
		},
		canChangeKind: canAdmin,
		canExportUsers: () => {
			if (!useExport) return false;
			return hasAnyPermission('CUSTOMERS_EXPORTER');
		},
		canImportUsers: () => {
			if (!useImport) return false;
			return hasAnyPermission('CUSTOMERS_IMPORTER');
		},
		canCreateUsers: canAdmin,
		canChangeCover: canAdmin,
	};

	return userPermissions;
};

export const useUserPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createUserPermissions(permissions), [permissions]);
};
